import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Languages as LanguagesIcon, TrendingUp, ClipboardList, Handshake,
  Globe2, MapPin, AlertTriangle, BookOpen,
} from "lucide-react";

const KPI_META = [
  { key: "total_dialects",        label: "Total Dialects",        Icon: LanguagesIcon, tone: "ink" },
  { key: "active",                label: "Active",                Icon: TrendingUp,    tone: "ontrack" },
  { key: "planned",               label: "In Planning",           Icon: ClipboardList, tone: "amber" },
  { key: "relationship",          label: "In Relationship",       Icon: Handshake,    tone: "accent" },
  { key: "countries",             label: "Countries",             Icon: Globe2,        tone: "ink" },
  { key: "active_countries",      label: "Active Countries",      Icon: MapPin,        tone: "ontrack" },
  { key: "languages_with_issues", label: "Languages With Issues", Icon: AlertTriangle, tone: "behind" },
];

const TONE_COLOR = {
  ontrack: "var(--on-track)", behind: "var(--behind)",
  amber: "var(--amber)", accent: "var(--accent)", ink: "var(--ink)",
};

const PRODUCT_COLORS = {
  "New Testament": "var(--on-track)",
  "Old Testament": "var(--accent)",
  "Bible Stories": "var(--amber)",
};

export default function Overview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/languages/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading || !data) {
    return <div className="p-10 mono text-xs uppercase tracking-widest">Loading dashboard…</div>;
  }
  const k = data.kpis;
  return (
    <div className="max-w-[1900px] mx-auto px-10 py-10 fade-in-up" data-testid="dashboard-page">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Global Command Board · From Language Engagements
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Translation Progress</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-2xl">
          Live view of every engagement across ISO × RoLV × Country, sourced from the language database.
        </p>
      </div>

      {/* Hero + KPI Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="hairline bg-white p-8 flex flex-col justify-between" data-testid="hero-total-languages">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-3">
              Total Languages
            </div>
            <div className="serif text-7xl tracking-tight leading-none">{k.total_languages.toLocaleString()}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] mt-3">
              Unique Language × ISO × RoLV × Country combinations
            </div>
          </div>
          <BookOpen size={28} strokeWidth={1.25} className="text-[color:var(--ink-3)] mt-6" />
        </div>
        <div className="col-span-2 grid grid-cols-3 gap-2">
          {KPI_META.map((meta) => (
            <KpiPill {...meta} key={meta.key} value={k[meta.key] ?? 0} />
          ))}
        </div>
      </div>

      {/* Distribution — 4 status buckets */}
      <div className="hairline bg-white p-6 grid grid-cols-4 gap-8 mb-6">
        <DistCol title="All Languages"       data={data.distributions.all}          color="var(--ink-2)" testid="dist-all" />
        <DistCol title="Active"               data={data.distributions.active}       color="var(--on-track)" testid="dist-active" />
        <DistCol title="Planned"              data={data.distributions.planned}      color="var(--amber)" testid="dist-planned" />
        <DistCol title="Relationship"         data={data.distributions.relationship} color="var(--accent)" testid="dist-relationship" />
      </div>

      {/* Product pies */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(data.pies).map(([product, v]) => (
          <DonutPie key={product} title={product} progress={v.in_progress} complete={v.complete} color={PRODUCT_COLORS[product]} />
        ))}
      </div>
    </div>
  );
}

function KpiPill({ label, value, Icon, tone }) {
  const color = TONE_COLOR[tone];
  return (
    <div className="hairline bg-white p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">{label}</div>
        <Icon size={15} className="text-[color:var(--ink-3)]" strokeWidth={1.5} />
      </div>
      <div className="serif text-4xl tracking-tight" style={{ color }}>{value.toLocaleString()}</div>
    </div>
  );
}

function DistCol({ title, data, color, testid }) {
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.count)), [data]);
  return (
    <div data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-3">{title}</div>
      {data.length === 0 && <div className="text-xs text-[color:var(--ink-3)] italic">No entries</div>}
      {data.slice(0, 10).map((d) => {
        const pct = Math.max(2, Math.round((d.count / max) * 100));
        return (
          <div key={d.country} className="mb-1.5">
            <div className="flex justify-between text-xs mb-0.5">
              <span className="truncate pr-2">{d.country}</span>
              <span className="mono text-[color:var(--ink-3)]">{d.count}</span>
            </div>
            <div className="h-2 bg-[color:var(--surface-2)]">
              <div className="h-full" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonutPie({ title, progress, complete, color }) {
  const total = progress + complete;
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
  const dash = pct * 2.51; // 2πr with r=40
  return (
    <div className="hairline bg-white p-5 flex items-center gap-5" data-testid={`pie-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <svg width="96" height="96" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="var(--surface-2)" strokeWidth="14" />
        <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="14"
          strokeDasharray={`${dash} ${251 - dash}`} transform="rotate(-90 50 50)" strokeLinecap="butt" />
        <text x="50" y="56" textAnchor="middle" fontSize="20" fontFamily="'IBM Plex Mono',monospace" fill="var(--ink)">{pct}%</text>
      </svg>
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">{title}</div>
        <div className="text-sm mt-1">
          <span className="mono font-semibold" style={{ color }}>{complete}</span>{" "}
          <span className="text-[color:var(--ink-2)]">complete</span>
          {" · "}
          <span className="mono font-semibold">{progress}</span>{" "}
          <span className="text-[color:var(--ink-2)]">in progress</span>
        </div>
        {total === 0 && (
          <div className="mono text-[10px] text-[color:var(--ink-3)] mt-1 italic">
            Mark Current Scope / Completed Products on the Languages page to populate.
          </div>
        )}
      </div>
    </div>
  );
}
